import { toast } from '@heroui/react';
import { useCallback, useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

import {
  Button,
  Description,
  FieldError,
  Input,
  Label,
  ListBox,
  Modal,
  Select,
  TextArea,
  TextField,
} from '@heroui/react';
import { trpc } from '@/lib/trpc';
import { useAppSelector } from '@/stores/hooks';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';
import {
  AI_BILLING_CURRENCIES,
  AI_BILLING_CURRENCY_LABELS,
  AI_BILLING_DIMENSIONS,
  AI_BILLING_RESOLUTIONS,
  AI_BILLING_UNITS,
  datetimeLocalToIso,
  normalizeBillingRuleInputParameters,
  toDatetimeLocalValue,
} from '../constants';
import {
  AiBillingRuleLinesEditor,
  EMPTY_RULE_LINE,
  type BillingRuleLineFormValues,
} from './AiBillingRuleLinesEditor';

const inputParametersSchema = z
  .object({
    hasVideoInput: z.boolean().optional(),
    hasAudioOutput: z.boolean().optional(),
    resolution: z.enum(AI_BILLING_RESOLUTIONS).optional(),
  })
  .optional();

const ruleLineSchema = z.object({
  inputParameters: inputParametersSchema,
  usage: z.enum(AI_BILLING_DIMENSIONS),
  unit: z.enum(AI_BILLING_UNITS),
  costPrice: z.coerce.number().min(0, '成本价不能为负数'),
  price: z.coerce.number().min(0, '售价不能为负数'),
});

const ruleSchema = z.object({
  modelId: z.string().min(1, '请选择模型'),
  version: z.coerce.number().int('版本须为整数').min(1, '版本至少为 1'),
  rule: z.array(ruleLineSchema).min(1, '至少添加一条计价规则'),
  currency: z.enum(AI_BILLING_CURRENCIES),
  effectiveFrom: z.string().min(1, '请填写生效开始时间'),
  effectiveTo: z.string().optional(),
  remark: z.string().optional(),
});

type RuleFormValues = z.infer<typeof ruleSchema>;

export type AiBillingRuleFormItem = {
  id?: string;
  modelId?: string | null;
  version?: number | null;
  rule?: BillingRuleLineFormValues[] | null;
  currency?: string | null;
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
  remark?: string | null;
};

interface AiBillingRuleEditorModalProps {
  open: boolean;
  rule?: AiBillingRuleFormItem | null;
  onCancel: () => void;
  onSuccess: () => void;
}

function normalizeRuleLines(raw: unknown): BillingRuleLineFormValues[] {
  if (!Array.isArray(raw) || raw.length === 0) return [{ ...EMPTY_RULE_LINE }];
  return raw.map((item) => {
    const row = item as Record<string, unknown>;
    const usage = AI_BILLING_DIMENSIONS.includes(row.usage as never)
      ? (row.usage as BillingRuleLineFormValues['usage'])
      : 'INPUT_TOKEN';
    const unit = AI_BILLING_UNITS.includes(row.unit as never)
      ? (row.unit as BillingRuleLineFormValues['unit'])
      : '1M_TOKEN';
    return {
      inputParameters: normalizeBillingRuleInputParameters(row.inputParameters),
      usage,
      unit,
      costPrice: Number(row.costPrice ?? 0),
      price: Number(row.price ?? 0),
    };
  });
}

export function AiBillingRuleEditorModal({
  open,
  rule,
  onCancel,
  onSuccess,
}: AiBillingRuleEditorModalProps) {
  const utils = trpc.useUtils();
  const isEdit = !!rule;
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const isHydrated = useAppSelector((state) => state.auth.isHydrated);

  const { data: modelsData } = trpc.aiModel.listAll.useQuery(
    {},
    { enabled: isHydrated && isAuthenticated && open }
  );

  const models = (modelsData ?? []) as Array<{
    id: string;
    modelName?: string | null;
    modelCode?: string | null;
    modelVersion?: string | null;
  }>;

  const handleModalOpenChange = useCallback((next: boolean) => {
    if (!next) onCancelRef.current();
  }, []);

  const form = useForm<RuleFormValues>({
    resolver: zodResolver(ruleSchema) as any,
    defaultValues: {
      modelId: '',
      version: 1,
      rule: [{ ...EMPTY_RULE_LINE }],
      currency: 'CNY',
      effectiveFrom: '',
      effectiveTo: '',
      remark: '',
    },
    mode: 'onChange',
  });
  const { reset, control, setValue, watch } = form;

  const createRuleMutation = trpc.aiBillingRule.create.useMutation({
    onSuccess: () => {
      toast.success('计费规则创建成功');
      form.reset();
      onSuccess();
      void utils.aiBillingRule.list.invalidate();
      void utils.aiBillingRule.listAll.invalidate();
    },
  });

  const updateRuleMutation = trpc.aiBillingRule.update.useMutation({
    onSuccess: () => {
      toast.success('计费规则更新成功');
      form.reset();
      onSuccess();
      void utils.aiBillingRule.list.invalidate();
      void utils.aiBillingRule.listAll.invalidate();
    },
  });

  useEffect(() => {
    if (!open) {
      reset();
      return;
    }
    if (isEdit && rule) {
      reset({
        modelId: rule.modelId || '',
        version: rule.version ?? 1,
        rule: normalizeRuleLines(rule.rule),
        currency: (AI_BILLING_CURRENCIES as readonly string[]).includes(rule.currency ?? '')
          ? (rule.currency as (typeof AI_BILLING_CURRENCIES)[number])
          : 'CNY',
        effectiveFrom: toDatetimeLocalValue(rule.effectiveFrom),
        effectiveTo: toDatetimeLocalValue(rule.effectiveTo),
        remark: rule.remark || '',
      });
    } else {
      reset({
        modelId: models[0]?.id ?? '',
        version: 1,
        rule: [{ ...EMPTY_RULE_LINE }],
        currency: 'CNY',
        effectiveFrom: toDatetimeLocalValue(new Date()),
        effectiveTo: '',
        remark: '',
      });
    }
  }, [open, isEdit, rule, reset, models]);

  const onSubmit = async (values: RuleFormValues) => {
    const effectiveFromIso = datetimeLocalToIso(values.effectiveFrom);
    if (!effectiveFromIso) {
      form.setError('effectiveFrom', { message: '生效开始时间格式无效' });
      return;
    }
    const effectiveToIso = values.effectiveTo?.trim()
      ? datetimeLocalToIso(values.effectiveTo)
      : null;
    if (values.effectiveTo?.trim() && !effectiveToIso) {
      form.setError('effectiveTo', { message: '生效结束时间格式无效' });
      return;
    }

    const payload = {
      modelId: values.modelId,
      version: values.version,
      rule: values.rule.map(({ inputParameters, ...line }) => ({
        ...line,
        ...(inputParameters ? { inputParameters } : {}),
      })),
      currency: values.currency,
      effectiveFrom: effectiveFromIso,
      effectiveTo: effectiveToIso,
      remark: values.remark?.trim() || null,
    };

    if (isEdit && rule?.id) {
      await updateRuleMutation.mutateAsync({ id: rule.id, data: payload });
    } else {
      await createRuleMutation.mutateAsync(payload);
    }
  };

  const isLoading = createRuleMutation.isPending || updateRuleMutation.isPending;
  const errors = form.formState.errors;

  return (
    <Modal isOpen={open} onOpenChange={handleModalOpenChange}>
      <RacDialogTriggerAnchor />
      <Modal.Backdrop>
        <Modal.Container placement="center" scroll="inside" size="cover" className="max-w-5xl">
          <Modal.Dialog
            key={open ? (isEdit && rule ? `edit-${String(rule.id ?? '')}` : 'create') : 'closed'}
          >
            <Modal.CloseTrigger />
            <Modal.Header className="gap-1">
              <Modal.Heading className="text-xl font-medium">
                {isEdit ? '编辑计费规则' : '创建计费规则'}
              </Modal.Heading>
              <Description className="text-sm">
                {isEdit ? '修改模型价格版本与计价项' : '为模型新增价格版本'}
              </Description>
            </Modal.Header>
            <Modal.Body className="gap-4 p-2">
              <form
                id="editor-ai-billing-rule-form"
                onSubmit={form.handleSubmit(onSubmit as any)}
                className="flex min-w-0 flex-col gap-4"
              >
                <Controller
                  control={form.control}
                  name="modelId"
                  render={({ field }) => (
                    <Select
                      className="w-full"
                      isInvalid={!!errors.modelId}
                      variant="secondary"
                      value={field.value || null}
                      onChange={(key) => {
                        if (key != null) field.onChange(String(key));
                      }}
                    >
                      <Label className="text-sm font-medium">
                        模型 <span className="text-danger">*</span>
                      </Label>
                      <Select.Trigger>
                        <Select.Value />
                        <Select.Indicator />
                      </Select.Trigger>
                      <Select.Popover>
                        <ListBox>
                          {models.map((m) => {
                            const label = `${m.modelName || m.modelCode || m.id}${m.modelVersion ? ` (${m.modelVersion})` : ''}`;
                            return (
                              <ListBox.Item key={m.id} id={m.id} textValue={label}>
                                {label}
                              </ListBox.Item>
                            );
                          })}
                        </ListBox>
                      </Select.Popover>
                      {errors.modelId ? <FieldError>{errors.modelId.message}</FieldError> : null}
                    </Select>
                  )}
                />

                <Controller
                  control={form.control}
                  name="version"
                  render={({ field }) => (
                    <TextField className="w-full" isInvalid={!!errors.version}>
                      <Label className="text-sm font-medium">
                        版本 <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="number"
                        min={1}
                        step={1}
                        placeholder="1"
                        variant="secondary"
                        value={String(field.value ?? '')}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                      <Description className="text-xs">同一模型下版本号唯一，建议递增</Description>
                      {errors.version ? <FieldError>{errors.version.message}</FieldError> : null}
                    </TextField>
                  )}
                />

                <AiBillingRuleLinesEditor
                  control={control}
                  setValue={setValue}
                  watch={watch}
                  errors={errors}
                />

                <Controller
                  control={form.control}
                  name="currency"
                  render={({ field }) => (
                    <Select
                      className="w-full"
                      isInvalid={!!errors.currency}
                      variant="secondary"
                      value={field.value}
                      onChange={(key) => {
                        if (key != null) field.onChange(String(key));
                      }}
                    >
                      <Label className="text-sm font-medium">
                        币种 <span className="text-danger">*</span>
                      </Label>
                      <Select.Trigger>
                        <Select.Value />
                        <Select.Indicator />
                      </Select.Trigger>
                      <Select.Popover>
                        <ListBox>
                          {AI_BILLING_CURRENCIES.map((c) => (
                            <ListBox.Item key={c} id={c} textValue={AI_BILLING_CURRENCY_LABELS[c]}>
                              {AI_BILLING_CURRENCY_LABELS[c]}
                            </ListBox.Item>
                          ))}
                        </ListBox>
                      </Select.Popover>
                      {errors.currency ? <FieldError>{errors.currency.message}</FieldError> : null}
                    </Select>
                  )}
                />

                <Controller
                  control={form.control}
                  name="effectiveFrom"
                  render={({ field }) => (
                    <TextField className="w-full" isInvalid={!!errors.effectiveFrom}>
                      <Label className="text-sm font-medium">
                        生效开始 <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="datetime-local"
                        step={1}
                        variant="secondary"
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                      {errors.effectiveFrom ? (
                        <FieldError>{errors.effectiveFrom.message}</FieldError>
                      ) : null}
                    </TextField>
                  )}
                />

                <Controller
                  control={form.control}
                  name="effectiveTo"
                  render={({ field }) => (
                    <TextField className="w-full" isInvalid={!!errors.effectiveTo}>
                      <Label className="text-sm font-medium">生效结束</Label>
                      <Input
                        type="datetime-local"
                        step={1}
                        variant="secondary"
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                      <Description className="text-xs">留空表示当前有效</Description>
                      {errors.effectiveTo ? (
                        <FieldError>{errors.effectiveTo.message}</FieldError>
                      ) : null}
                    </TextField>
                  )}
                />

                <TextField className="w-full" isInvalid={!!errors.remark}>
                  <Label className="text-sm font-medium" htmlFor="editor-billing-rule-remark">
                    备注
                  </Label>
                  <TextArea
                    id="editor-billing-rule-remark"
                    aria-label="备注"
                    className="resize-none"
                    variant="secondary"
                    placeholder="可选，如调价原因"
                    rows={3}
                    {...form.register('remark')}
                  />
                  {errors.remark ? <FieldError>{errors.remark.message}</FieldError> : null}
                </TextField>
              </form>
            </Modal.Body>
            <Modal.Footer>
              <Button type="button" variant="ghost" onPress={onCancel}>
                取消
              </Button>
              <Button
                isDisabled={isLoading}
                variant="primary"
                onPress={() => void form.handleSubmit(onSubmit as any)()}
              >
                {isLoading ? '提交中…' : isEdit ? '更新' : '创建'}
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}
